/**
 * Calcula longitud total y cantidad de entidades por capa del DXF.
 *
 * Recorre top-level entities — los bloques referenciados por INSERT NO se
 * expanden en esta v1 (el modal de selección apunta sólo a capas explícitas
 * del DXF, no a geometría virtual de bloques). Si más adelante hay que
 * incluirlas, expandir aquí resolviendo `cadJson.blocks[insert.name]` con
 * la transformación del INSERT.
 *
 * Unidades: la longitud se devuelve "como viene" en las coordenadas del DXF
 * sin aplicar factor por $INSUNITS. Esto mantiene consistencia con
 * `_GetEntityTotalLength` del LayoutBuilder (que también usa las coords
 * directas para mostrar metros en el modal de avance). Antes aplicábamos
 * el factor de unidades pero generaba discrepancia: el modal de avance
 * mostraba 42m y este modal mostraba 0.42m para la misma polilínea cuando el
 * DXF declaraba `$INSUNITS=5` (cm). Si en el futuro algún DXF llega en mm/cm
 * y necesita conversión real, el caller puede usar getUnitFactor() expuesto
 * abajo y multiplicar al final.
 *
 * Tipos soportados: LINE, LWPOLYLINE/POLYLINE, ARC, CIRCLE.
 * El resto (TEXT, MTEXT, INSERT, SOLID, HATCH, etc.) suma `count` pero no
 * aporta longitud — útil para detectar capas "no-geométricas" en el modal.
 */

// Factor de conversión a metros por código $INSUNITS (AutoCAD DXF reference).
// Solo cubrimos las unidades comunes; el resto cae al default 1.0.
const INSUNITS_TO_METERS = {
  0: 1,       // unitless — asumimos metros
  1: 0.0254,  // pulgadas
  2: 0.3048,  // pies
  3: 1609.344, // millas
  4: 0.001,   // milímetros
  5: 0.01,    // centímetros
  6: 1,       // metros
  7: 1000,    // kilómetros
  8: 2.54e-8, // microinches
  9: 2.54e-5, // mils
  10: 0.9144, // yardas
  11: 1e-10,  // angstroms
  12: 1e-9,   // nanómetros
  13: 1e-6,   // micras
  14: 0.1,    // decímetros
  15: 10,     // decámetros
  16: 100,    // hectómetros
}

export function getUnitFactor(cadJson) {
  const code = cadJson?.header?.$INSUNITS
  if (code == null) return 1
  return INSUNITS_TO_METERS[code] ?? 1
}

// Suma defensiva: ignora segmentos con coords NaN/Infinity para no contaminar
// el total con un NaN que invalide toda la longitud de la capa.
function safeSegment(ax, ay, bx, by) {
  if (!Number.isFinite(ax) || !Number.isFinite(ay) ||
      !Number.isFinite(bx) || !Number.isFinite(by)) return 0
  const d = Math.hypot(bx - ax, by - ay)
  return Number.isFinite(d) ? d : 0
}

function lineLength(e) {
  if (!e?.vertices || e.vertices.length < 2) return 0
  const a = e.vertices[0]
  const b = e.vertices[1]
  return safeSegment(a.x, a.y, b.x, b.y)
}

function polylineLength(e) {
  const verts = e?.vertices
  if (!verts || verts.length < 2) return 0
  let total = 0
  for (let i = 0; i < verts.length - 1; i++) {
    total += safeSegment(verts[i].x, verts[i].y, verts[i + 1].x, verts[i + 1].y)
  }
  // Cierre opcional (LWPOLYLINE flag 1 / POLYLINE closed)
  if (e.shape === true || e.closed === true) {
    const a = verts[0]
    const b = verts[verts.length - 1]
    total += safeSegment(a.x, a.y, b.x, b.y)
  }
  return total
}

function arcLength(e) {
  // ARC: startAngle/endAngle en radianes según el parser de dxf-parser
  const r = e?.radius
  if (!r) return 0
  let start = e.startAngle
  let end   = e.endAngle
  if (start == null || end == null) return 0
  let sweep = end - start
  if (sweep < 0) sweep += Math.PI * 2
  return Math.abs(sweep) * r
}

function circleLength(e) {
  const r = e?.radius
  if (!r) return 0
  return 2 * Math.PI * r
}

function entityLength(e) {
  switch (e?.type) {
    case 'LINE':       return lineLength(e)
    case 'POLYLINE':
    case 'LWPOLYLINE': return polylineLength(e)
    case 'ARC':        return arcLength(e)
    case 'CIRCLE':     return circleLength(e)
    default:           return 0
  }
}

/**
 * @param {Object} cadJson - resultado del parser DXF (con .entities y .header)
 * @returns {Object<string, {length: number, count: number, hasGeometry: boolean}>}
 *   length en metros, count = cuántas entidades viven en la capa,
 *   hasGeometry = si al menos una contribuye a length (útil para filtrar
 *   capas que sólo contienen texto / inserts).
 */
export function computeLayerMetrics(cadJson) {
  const out = Object.create(null)
  const raw = cadJson?.entities
  if (!raw) return out
  const list = Array.isArray(raw) ? raw : Object.values(raw)

  for (const e of list) {
    const layer = e?.layer
    if (!layer) continue
    if (!out[layer]) out[layer] = { length: 0, count: 0, hasGeometry: false }
    out[layer].count += 1
    const len = entityLength(e)
    if (len > 0) {
      out[layer].length += len
      out[layer].hasGeometry = true
    }
  }
  return out
}
